import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import { Button } from '../../../../shared/components/Button.js';
import {
  listPlanningCycles,
  currentCycleId,
  listBrandingTaxonomy,
  type PlanningCycle,
  type TaxonomyRow,
} from '../../api/vendorBrandingProfileApi.js';
import {
  getCompanyBrandingProfile,
  saveCompanyBrandingProfile,
  type CompanyGeographyRow,
  type GeoScopeLevel,
} from '../../api/companyBrandingProfileApi.js';
import { GeographyCascade, EMPTY_GEO, type GeoSelection } from '../m17/GeographyCascade.js';

/** t626 holds a state, a district or a sub-district — nothing deeper. */
const LEVEL_OF: Record<string, GeoScopeLevel> = {
  GEO_STATE: 'STATE',
  GEO_DISTRICT: 'DISTRICT',
  GEO_TEHSIL: 'SUBDISTRICT',
};

const ID_FIELD_OF: Record<GeoScopeLevel, 'stateId' | 'districtId' | 'tehsilId' | null> = {
  ALL_INDIA: null,
  STATE: 'stateId',
  DISTRICT: 'districtId',
  SUBDISTRICT: 'tehsilId',
};

const keyOf = (typeId: string, itemId: string | null) => `${typeId}|${itemId ?? ''}`;

interface Props {
  readonly companyId: string;
}

/**
 * What branding work this company runs, and where, for one financial year.
 *
 * Advisory by design: it narrows budget planning and is the boundary a vendor
 * profile is drawn inside. It does not block work orders — that gate already
 * lives on the vendor profile, and a second one could disagree with it.
 */
export function CompanyBrandingProfileTab({ companyId }: Props): ReactNode {
  const { token } = useAuth();
  const [cycles, setCycles] = useState<readonly PlanningCycle[]>([]);
  const [cycleId, setCycleId] = useState('');
  // null until read: an unread taxonomy must not read as "none configured".
  const [taxonomy, setTaxonomy] = useState<readonly TaxonomyRow[] | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const [geography, setGeography] = useState<readonly CompanyGeographyRow[]>([]);
  const [pending, setPending] = useState<GeoSelection>(EMPTY_GEO);
  const [remarks, setRemarks] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    Promise.all([listPlanningCycles(token), listBrandingTaxonomy(token)])
      .then(([c, tx]) => {
        if (!alive) return;
        setCycles(c);
        setTaxonomy(tx);
        setCycleId((p) => p || currentCycleId(c));
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    let alive = true;
    if (!companyId || !cycleId) return;
    setStatus(null);
    getCompanyBrandingProfile(companyId, cycleId, token)
      .then((p) => {
        if (!alive) return;
        setTicked(new Set(p.scope.map((s) => keyOf(s.brandingTypeId, s.brandingItemId))));
        setGeography(p.geography);
        setRemarks(p.remarks ?? '');
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [companyId, cycleId, token]);

  /** Types with their sub-types, from the tenant's own taxonomy. */
  const grouped = useMemo(() => {
    const m = new Map<string, { typeName: string; items: TaxonomyRow[] }>();
    for (const r of taxonomy ?? []) {
      const g = m.get(r.brandingTypeId) ?? { typeName: r.typeName, items: [] };
      if (r.brandingItemId) g.items.push(r);
      m.set(r.brandingTypeId, g);
    }
    return [...m.entries()];
  }, [taxonomy]);

  const toggle = (typeId: string, itemId: string | null) => {
    setStatus(null);
    setTicked((prev) => {
      const next = new Set(prev);
      const k = keyOf(typeId, itemId);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const addGeography = () => {
    setStatus(null);
    const level = pending.geoLevelCode ? LEVEL_OF[pending.geoLevelCode] : 'ALL_INDIA';
    if (!level) return;
    const field = ID_FIELD_OF[level];
    const row: CompanyGeographyRow = {
      scopeId: `draft-${Math.random().toString(36).slice(2)}`,
      scopeType: level,
      stateId: null,
      districtId: null,
      tehsilId: null,
      label: pending.geoNodeName || 'All India',
      ...(field ? { [field]: pending.geoNodeId } : {}),
    } as CompanyGeographyRow;

    // The same place twice would be rejected by the server anyway.
    const already = geography.some(
      (g) => g.scopeType === row.scopeType && (field ? g[field] === pending.geoNodeId : true),
    );
    if (already) {
      setError('That coverage is already listed.');
      return;
    }
    setError(null);
    setGeography((gs) => [...gs, row]);
  };

  const removeGeography = (scopeId: string) => {
    setStatus(null);
    setGeography((gs) => gs.filter((g) => g.scopeId !== scopeId));
  };

  const save = async () => {
    if (!cycleId) return;
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const saved = await saveCompanyBrandingProfile(
        {
          companyId,
          planningCycleId: cycleId,
          remarks: remarks || undefined,
          scope: [...ticked].map((k) => {
            const [brandingTypeId, itemId] = k.split('|');
            return { brandingTypeId, brandingItemId: itemId || undefined };
          }),
          geography: geography.map((g) => ({
            scopeType: g.scopeType as GeoScopeLevel,
            stateId: g.stateId ?? undefined,
            districtId: g.districtId ?? undefined,
            tehsilId: g.tehsilId ?? undefined,
          })),
        },
        token,
      );
      setGeography(saved.geography);
      setTicked(new Set(saved.scope.map((s) => keyOf(s.brandingTypeId, s.brandingItemId))));
      setStatus('Company profile saved');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section aria-label="company-branding-profile">
      {error ? <p role="alert">{error}</p> : null}
      {status ? <p role="status" data-testid="cbp-saved">{status}</p> : null}

      <label htmlFor="cbp-cycle">Financial year</label>
      <select
        id="cbp-cycle"
        value={cycleId}
        onChange={(e) => setCycleId(e.target.value)}
        data-testid="cbp-cycle"
      >
        <option value="">Select a year…</option>
        {cycles.map((c) => (
          <option key={c.planningCycleId} value={c.planningCycleId}>{c.cycleName}</option>
        ))}
      </select>

      <fieldset data-testid="cbp-scope">
        <legend>Branding work this company runs</legend>
        {taxonomy !== null && grouped.length === 0 ? (
          <p data-testid="cbp-no-taxonomy">No branding types are configured yet.</p>
        ) : null}
        {grouped.map(([typeId, g]) => (
          <div key={typeId}>
            <label>
              <input
                type="checkbox"
                checked={ticked.has(keyOf(typeId, null))}
                onChange={() => toggle(typeId, null)}
                data-testid={`cbp-type-${typeId}`}
              />
              {g.typeName}
            </label>
            {g.items.map((it) => (
              <label key={it.brandingItemId!}>
                <input
                  type="checkbox"
                  checked={ticked.has(keyOf(typeId, it.brandingItemId))}
                  onChange={() => toggle(typeId, it.brandingItemId)}
                  data-testid={`cbp-item-${it.brandingItemId}`}
                />
                {it.itemName}
              </label>
            ))}
          </div>
        ))}
      </fieldset>

      <fieldset data-testid="cbp-geography">
        <legend>Where it operates</legend>
        {/* Stops at sub-district because that is the deepest t626 can hold. */}
        <GeographyCascade
          idPrefix="cbp-geo"
          testIdPrefix="cbp-geo"
          deepestLevelCode="GEO_TEHSIL"
          onChange={setPending}
        />
        <Button type="button" variant="secondary" onClick={addGeography} data-testid="cbp-add-geo">
          Add {pending.geoNodeName || 'All India'}
        </Button>

        <ul>
          {geography.map((g) => (
            <li key={g.scopeId} data-testid={`cbp-geo-row-${g.scopeId}`}>
              {g.label}
              <Button
                type="button"
                variant="secondary"
                onClick={() => removeGeography(g.scopeId)}
                data-testid={`cbp-geo-remove-${g.scopeId}`}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </fieldset>

      <label htmlFor="cbp-remarks">Remarks</label>
      <input
        id="cbp-remarks"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        data-testid="cbp-remarks"
      />

      <Button
        type="button"
        variant="primary"
        onClick={() => void save()}
        disabled={busy || !cycleId}
        data-testid="cbp-save"
      >
        {busy ? 'Saving…' : 'Save company profile'}
      </Button>
    </section>
  );
}
